import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAssistantRuntime,
  useThreadListItem
} from "@assistant-ui/react";
import { ArchiveIcon, ArchiveRestoreIcon, Loader2, PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "./confirm-dialog";
import { useAgentContext } from "../context/AgentContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { threadPaginationManager, triggerThreadListRefresh } from "@/lib/remote-runtime-provider";

export const ThreadList = ({
  isLogsAgentPage,
}: {
  isLogsAgentPage?: boolean;
}) => {
  const { t } = useTranslation();
  const assistantRuntime = useAssistantRuntime();
  const { selectedAgent } = useAgentContext();
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    threadId: string | null;
    action: "archive" | "unarchive";
  }>({
    isOpen: false,
    threadId: null,
    action: "archive",
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Sync hasMore state with threadPaginationManager whenever threads change
  useEffect(() => {
    if (!assistantRuntime) return;

    // Initial sync
    setHasMore(threadPaginationManager.getHasMore());

    // Subscribe to thread list changes
    const unsubscribe = assistantRuntime.threads.subscribe(() => {
      setHasMore(threadPaginationManager.getHasMore());
    });

    return () => unsubscribe();
  }, [assistantRuntime]);

  const handleArchiveClick = (
    threadId: string,
    action: "archive" | "unarchive"
  ) => {
    setConfirmDialog({
      isOpen: true,
      threadId,
      action,
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.threadId) return;

    try {
      const isArchiving = confirmDialog.action === "archive";

      // Use the assistant runtime's database adapter methods
      const runtime = assistantRuntime.threadList as any;

      // await updateThread(confirmDialog.threadId, { archived: isArchiving });
      // await runtime._core._options.adapter.list();
      const item = runtime.getItemById(confirmDialog.threadId); // thread id you passed above

      if (isArchiving) {
        await item.archive();
      } else {
        await item.unarchive();
      }

      console.log(
        `Thread ${isArchiving ? "archived" : "unarchived"} successfully:`,
        confirmDialog.threadId
      );
    } catch (error) {
      console.error(`Failed to ${confirmDialog.action} thread:`, error);
      // You might want to show an error toast here
    } finally {
      handleCloseConfirm();
    }
  };

  const handleCloseConfirm = () => {
    setConfirmDialog({
      isOpen: false,
      threadId: null,
      action: "archive",
    });
  };

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const agentId = selectedAgent?.id;
      await threadPaginationManager.loadMore(agentId);

      // Update hasMore state
      setHasMore(threadPaginationManager.getHasMore());

      // Trigger thread list refresh
      triggerThreadListRefresh();
    } catch (error) {
      console.error("Failed to load more threads:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getDialogContent = () => {
    const isArchiving = confirmDialog.action === "archive";
    return {
      title: isArchiving ? t("chat.archiveThread") : t("chat.unarchiveThread"),
      message: isArchiving
        ? t("chat.archiveThreadConfirm")
        : t("chat.unarchiveThreadConfirm"),
      confirmText: isArchiving ? t("chat.archive") : t("chat.unarchive"),
    };
  };

  const dialogContent = getDialogContent();
  return (
    <>
      <ThreadListPrimitive.Root
        className={cn("flex h-[calc(100vh-60px)] flex-col ", {
          ["h-full"]: isLogsAgentPage,
        })}
      >
        <div className="p-2">
          <ThreadListNew />
        </div>
        <div className="chat-scrollbar flex-1 overflow-y-auto">
          <div className="p-2">
            <ThreadListItems onArchiveClick={handleArchiveClick} />
          </div>
          {hasMore && (
            <div className="px-2 pb-2">
              <Button
                className="hover:bg-sidebar-accent/50 w-full justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-normal transition-all duration-200"
                variant="ghost"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                style={{
                  color: "var(--sidebar-foreground)",
                  opacity: isLoadingMore ? 0.6 : 0.7,
                }}
                onMouseEnter={(e) => {
                  if (!isLoadingMore) {
                    e.currentTarget.style.opacity = "1";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoadingMore) {
                    e.currentTarget.style.opacity = "0.7";
                  }
                }}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{t("common.loading")}</span>
                  </>
                ) : (
                  <span>{t("chat.loadMore")}</span>
                )}
              </Button>
            </div>
          )}
        </div>
      </ThreadListPrimitive.Root>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmAction}
        title={dialogContent.title}
        message={dialogContent.message}
        confirmText={dialogContent.confirmText}
        cancelText={t("common.cancel")}
        variant="danger"
      />
    </>
  );
};

const ThreadListNew: FC = () => {
  const { t } = useTranslation();
  const { selectedAgent } = useAgentContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const isLogsAgentPage = currentPath?.endsWith("/logs");

  const handleNewChatClick = () => {
    if (!selectedAgent) return;

    navigate(`/chat/${selectedAgent.id}`);
  };

  if (isLogsAgentPage) return;

  return (
    <ThreadListPrimitive.New asChild>
      <Button
        className="bg-sidebar-accent hover:bg-sidebar-accent/80 w-full justify-start gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
        variant="ghost"
        disabled={!selectedAgent}
        onClick={handleNewChatClick}
        style={{
          color: "var(--sidebar-foreground)",
        }}
        onMouseEnter={(e) => {
          if (selectedAgent) {
            e.currentTarget.style.backgroundColor = "var(--sidebar-border)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "var(--sidebar-accent)";
        }}
      >
        <PlusIcon className="h-4 w-4" />
        {t("chat.newChat")}
      </Button>
    </ThreadListPrimitive.New>
  );
};

const ThreadListItems: FC<{
  onArchiveClick: (threadId: string, action: "archive" | "unarchive") => void;
}> = ({ onArchiveClick }) => {
  return (
    <ThreadListPrimitive.Items
      components={{
        ThreadListItem: (props) => (
          <ThreadListItem {...props} onArchiveClick={onArchiveClick} />
        ),
      }}
    />
  );
};

const ThreadListItem: FC<{
  onArchiveClick: (threadId: string, action: "archive" | "unarchive") => void;
}> = ({ onArchiveClick, ...props }) => {
  const { availableAgents, setSelectedAgent } = useAgentContext();
  const threadListItem = useThreadListItem();
  const location = useLocation();
  const currentPath = location.pathname;
  const isLogsAgentPage = currentPath?.endsWith("/logs");
  const navigate = useNavigate();

  const handleSelectThread = () => {
    const agentId = (threadListItem as any).externalId;
    if (agentId) {
      const agent = availableAgents.find((a) => a.id === agentId);
      if (agent) {
        setSelectedAgent(agent);
      }
    }

    if (threadListItem.remoteId && !isLogsAgentPage) {
      navigate(`/chat/${agentId}/${threadListItem.remoteId}`);
    }
  };

  return (
    <ThreadListItemPrimitive.Root className="thread-list-item group">
      <ThreadListItemPrimitive.Trigger
        onClick={handleSelectThread}
        className="flex w-full items-center justify-between rounded-lg text-left text-sm transition-colors duration-200"
      >
        <ThreadListItemTitle />
        {!isLogsAgentPage && (
          <ThreadListItemArchive onArchiveClick={onArchiveClick} />
        )}
      </ThreadListItemPrimitive.Trigger>
    </ThreadListItemPrimitive.Root>
  );
};

const ThreadListItemTitle: FC = () => {
  const { t } = useTranslation();
  return (
    <p className="truncate text-sm">
      <ThreadListItemPrimitive.Title fallback={t("chat.newChat")} />
    </p>
  );
};

const ThreadListItemArchive: FC<{
  onArchiveClick: (threadId: string, action: "archive" | "unarchive") => void;
}> = ({ onArchiveClick }) => {
  const { t } = useTranslation();
  const threadListItem = useThreadListItem();
  const isArchived = threadListItem.status === "archived";

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering the thread selection

    // Get the actual thread ID from the primitive
    const threadId = threadListItem.remoteId;
    if (threadId) {
      const action = isArchived ? "unarchive" : "archive";
      onArchiveClick(threadId, action);
    }
  };

  return (
    <div onClick={handleClick}>
      <div
        className="hover:bg-sidebar-accent/50 flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm p-0 opacity-0 transition-all duration-200 group-hover:opacity-100"
        title={isArchived ? t("chat.unarchiveThread") : t("chat.archiveThread")}
        style={{
          color: "var(--sidebar-foreground)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--sidebar-primary)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--sidebar-foreground)";
        }}
      >
        {isArchived ? (
          <ArchiveRestoreIcon className="h-4 w-4" />
        ) : (
          <ArchiveIcon className="h-4 w-4" />
        )}
      </div>
    </div>
  );
};
